import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ResponderWorkContextService } from '@crowdctrl/shared';
import { readSessionRouteParams } from './joined-session.guard';

/** Join + policy-sync must stay reachable; everything else under my-orders waits for sync when pending. */
function shouldBypassPolicySyncRedirect(url: string): boolean {
  return /\/my-orders\/join(\/|$|\?)/.test(url) || /\/my-orders\/policy-sync(\/|$|\?)/.test(url);
}

/**
 * Blocks My Work, Order Detail, and other guarded work routes while
 * `orderCopyPolicySyncPending` is true (redirect to policy-sync with replaceUrl).
 * If work-context cannot be loaded, allow navigation (do not trap offline / transient API errors).
 *
 * Always fetches work context here so the sync-pending decision is not based on stale in-memory cache.
 */
export const policySyncPendingGuard: CanActivateFn = async (route, state) => {
  if (shouldBypassPolicySyncRedirect(state.url)) {
    return true;
  }

  const router = inject(Router);
  const responderWorkContext = inject(ResponderWorkContextService);
  const { workspaceId, sessionId } = readSessionRouteParams(route);

  if (!workspaceId || !sessionId) {
    return true;
  }

  const toPolicySync = [
    '/workspaces',
    workspaceId,
    'sessions',
    sessionId,
    'my-orders',
    'policy-sync',
  ];

  try {
    const wc = await firstValueFrom(responderWorkContext.fetch(workspaceId, sessionId));
    if (wc?.responderState?.orderCopyPolicySyncPending === true) {
      return new RedirectCommand(router.createUrlTree(toPolicySync), {
        replaceUrl: true,
      });
    }
    return true;
  } catch {
    return true;
  }
};
